import React from "react";
import {Button, Dialog, DialogContent, FormControl, TextField, Typography} from "@mui/material";
import Divider from "@mui/material/Divider";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const Evaluation = ({ evaluation, onClose, firstName }) => {
  const [values, setValues] = React.useState({});
  const { t } = useTranslation();

  React.useEffect(() => {
    if (evaluation) {
      setValues({ evaluation: evaluation });
    }
  }, [evaluation]);

    return (

        <Dialog
            open={Boolean(evaluation)}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    position: "relative",
                    overflow: "visible",
                },
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{
                    position: "absolute",
                    top: 12,
                    right: 20,
                    backgroundColor: "#f0f4ff", // light background
                    color: "#1D6ADE",           // blue icon
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    "&:hover": {
                        backgroundColor: "#e0ebff",
                    },
                    zIndex: 1,
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ p: 3 }}>
                <FormControl noValidate autoComplete="off" fullWidth>
                    <Div
                        sx={{
                            width: "100%",
                            minHeight: 100,
                            minWidth: {xs: "90%", sm: 400, md: 500},
                        }}
                    >
                        {/* Title */}
                        <Typography
                            variant="h6"
                            sx={{
                                textAlign: "center",
                                fontWeight: "bold",
                                color: "#002F6C",
                                mb: 2,
                                mt:5,
                                fontSize: "1.3rem",
                                borderBottom: "3px solid #002F6C",
                                pb: 0.5,
                                width: "fit-content",
                                mx: "auto",
                            }}
                        >
                            {t("feedbackForm.info2")} {firstName}
                        </Typography>

                        {/* Overall Comment */}
                        <div>
                            <Typography
                                sx={{fontWeight: "bold", mb: 0.5, color: "#002F6C"}}
                            >
                                {t("evaluation-form.overall")}:
                            </Typography>

                            <TextField
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={10}
                                value={values.evaluation?.importantInformation || "-"}
                                disabled
                                sx={{
                                    bgcolor: "#FFF5E5",
                                    borderRadius: 2,
                                    mb: 2,
                                    "& .MuiInputBase-input.Mui-disabled": {
                                        WebkitTextFillColor: "#002F6C", // keep text black when disabled
                                    },
                                    "& .MuiInputBase-input": {
                                        scrollbarWidth: "thin",
                                        scrollbarColor: "#FF7F32 #FFF5E5", // Firefox
                                        "&::-webkit-scrollbar": {
                                            width: "6px",
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                            backgroundColor: "#FF7F32", // orange scroll
                                            borderRadius: "8px",
                                        },
                                        "&::-webkit-scrollbar-track": {
                                            backgroundColor: "#FFF5E5",
                                        },
                                    },
                                }}
                                variant="outlined"
                            />
                        </div>


                        {/* Example field: Actual Level */}
                        <Div
                            sx={{
                                display: "flex",
                                alignItems: { xs: "flex-start", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                mb: 2,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                    mr: 1,
                                    color: "#002F6C",
                                }}
                            >
                                {t("evaluation-form.actualLevel")}:
                            </Typography>

                            <TextField
                                fullWidth
                                value={values.evaluation?.actualLevel2?.label || "---"}
                                disabled
                                sx={{
                                    bgcolor: "#FFF5E5",
                                    borderRadius: 2,
                                    "& .MuiInputBase-input.Mui-disabled": {
                                        WebkitTextFillColor: "#002F6C", // keep text black when disabled
                                    },
                                }}
                                variant="outlined"
                                size="small"
                            />
                        </Div>

                        {/* Objective Level */}

                        <Div
                            sx={{
                                display: "flex",
                                alignItems: { xs: "flex-start", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                mb: 2,
                            }}
                        >
                            {/* Label */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                    mr: 1,
                                    color: "#002F6C",
                                }}
                            >
                                {t("evaluation-form.targetLevel")}:
                            </Typography>

                            {/* Value Box */}
                            <TextField
                                fullWidth
                                value={values.evaluation?.objectiveLevel2?.label || "---"}
                                disabled
                                sx={{
                                    bgcolor: "#FFF5E5",
                                    borderRadius: 2,
                                    "& .MuiInputBase-input.Mui-disabled": {
                                        WebkitTextFillColor: "#002F6C", // keep text black when disabled
                                    },
                                }}
                                variant="outlined"
                                size="small"
                            />
                        </Div>

                        {/* Book Recommended */}

                        <Div
                            sx={{
                                display: "flex",
                                alignItems: { xs: "flex-start", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                mb: 2,
                            }}
                        >
                            {/* Label */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                    mr: 1,
                                    color: "#002F6C",
                                }}
                            >
                                {t("evaluation-form.booksRecommended")}:
                            </Typography>

                            {/* Value Box */}
                            <TextField
                                fullWidth
                                value={
                                    values.evaluation?.booksRecommended &&
                                    values.evaluation?.booksRecommended.length > 0
                                        ? values.evaluation?.booksRecommended
                                        : "---"
                                }
                                disabled
                                sx={{
                                    bgcolor: "#FFF5E5",
                                    borderRadius: 2,
                                    "& .MuiInputBase-input.Mui-disabled": {
                                        WebkitTextFillColor: "#002F6C", // keep text black when disabled
                                    },
                                }}
                                variant="outlined"
                                size="small"
                            />
                        </Div>

                        {/* Teachers Recommended */}

                        <div>
                            <Typography
                                sx={{ fontWeight: "bold", mb: 0.5, color: "#002F6C" }}
                            >
                                {t("evaluation-form.teachersRecommended")}:
                            </Typography>

                            <TextField
                                fullWidth
                                value={
                                    values.evaluation?.teachers &&
                                    values.evaluation.teachers.length > 0
                                        ? values.evaluation.teachers
                                            .map((item) => `${item.firstName} ${item.lastName}`)
                                            .join("\n")
                                        : "No teachers recommended"
                                }
                                disabled
                                multiline
                                minRows={1} // 🔑 will auto-grow but never shrink smaller than 1 row
                                maxRows={10} // optional limit so it doesn’t get too tall
                                sx={{
                                    bgcolor: "#FFF5E5",
                                    borderRadius: 2,
                                    mb: 2,
                                    "& .MuiInputBase-input.Mui-disabled": {
                                        WebkitTextFillColor: "#002F6C", // keep text black
                                    },
                                }}
                                variant="outlined"
                            />
                        </div>

                        {/* Program Frequency */}

                        <Div
                            sx={{
                                display: "flex",
                                alignItems: { xs: "flex-start", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                mb: 2,
                            }}
                        >
                            {/* Label */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                    mr: 1,
                                    color: "#002F6C",
                                }}
                            >
                                {t("evaluation-form.frequencyRecommended")}:
                            </Typography>

                            {/* Value Box */}
                            <TextField
                                fullWidth
                                value={
                                    values.evaluation?.programFrequencyRecommended &&
                                    values.evaluation?.programFrequencyRecommended.length > 0
                                        ? values.evaluation?.programFrequencyRecommended
                                        : "---"
                                }
                                disabled
                                sx={{
                                    bgcolor: "#FFF5E5",
                                    borderRadius: 2,
                                    "& .MuiInputBase-input.Mui-disabled": {
                                        WebkitTextFillColor: "#002F6C", // keep text black when disabled
                                    },
                                }}
                                variant="outlined"
                                size="small"
                            />
                        </Div>

                        <Div
                            sx={{
                                display: "flex",
                                alignItems: { xs: "flex-start", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                mb: 2,
                            }}
                        >
                            {/* Label */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                    mr: 1,
                                    color: "#002F6C",
                                }}
                            >
                                {t("evaluation-form.acceptHomework")}:
                            </Typography>

                            {/* Value Box */}
                            <TextField
                                fullWidth
                                value={
                                    values.evaluation?.homeworkLesson
                                        ? values.evaluation?.homeworkLesson.toLowerCase() === "yes"
                                            ? "Yes"
                                            : values.evaluation?.homeworkLesson.toLowerCase() === "no"
                                                ? "No"
                                                : values.evaluation?.homeworkLesson
                                        : "---"
                                }
                                disabled
                                sx={{
                                    bgcolor: "#FFF5E5",
                                    borderRadius: 2,
                                    "& .MuiInputBase-input.Mui-disabled": {
                                        WebkitTextFillColor: "#002F6C", // keep text black when disabled
                                    },
                                }}
                                variant="outlined"
                                size="small"
                            />
                        </Div>

                        <Div
                            sx={{
                                display: "flex",
                                alignItems: { xs: "flex-start", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                mb: 2,
                            }}
                        >
                            {/* Label */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                    mr: 1,
                                    color: "#002F6C",
                                }}
                            >
                                {t("evaluation-form.acceptSubstitute")}:
                            </Typography>

                            {/* Value Box */}
                            <TextField
                                fullWidth
                                value={
                                    values.evaluation?.substituteTeacher
                                        ? values.evaluation?.substituteTeacher.toLowerCase() === "yes"
                                            ? "Yes"
                                            : values.evaluation?.substituteTeacher.toLowerCase() === "no"
                                                ? "No"
                                                : values.evaluation?.substituteTeacher
                                        : "---"
                                }
                                disabled
                                sx={{
                                    bgcolor: "#FFF5E5",
                                    borderRadius: 2,
                                    "& .MuiInputBase-input.Mui-disabled": {
                                        WebkitTextFillColor: "#002F6C", // keep text black when disabled
                                    },
                                }}
                                variant="outlined"
                                size="small"
                            />
                        </Div>


                    </Div>
                </FormControl>
            </DialogContent>
        </Dialog>
    );

    return (
        <Div sx={{width: "100%", maxWidth: 400, minHeight: 100}}>
            <IconButton
                onClick={onClose}
                sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "#002F6C",
                }}
            >
                <CloseIcon/>
            </IconButton>
            <Div sx={{mt: 3}}>
                <FormControl noValidate autoComplete="off">
                    <Div
                        sx={{
                            "& .MuiTextField-root": {
                                mb: 1.4,
                                mt: 0.5,
                            },
                        }}
                    >
                        <Div
                            sx={{
                                width: "100%",
                                minHeight: 100,
                                minWidth: {xs: "90%", sm: 400, md: 500},
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            {/* Title */}
                            <Typography
                                variant="h6"
                                sx={{
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    color: "#002F6C",
                                    mb: 2,
                                    fontSize: "1.3rem",
                                    borderBottom: "3px solid #002F6C", // underline effect
                                    pb: 0.5,
                                    width: "fit-content", // shrink to content
                                    mx: "auto",
                                }}
                            >
                                {t("feedbackForm.info2")} {firstName}
                            </Typography>

              {/* Overall Comment */}
              <div>
                <Typography
                  sx={{ fontWeight: "bold", mb: 0.5, color: "#002F6C" }}
                >
                  {t("evaluation-form.overall")}:
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={10}
                  value={values.evaluation?.importantInformation || "-"}
                  disabled
                  sx={{
                    bgcolor: "#FFF5E5",
                    borderRadius: 2,
                    mb: 2,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#002F6C", // keep text black when disabled
                    },
                    "& .MuiInputBase-input": {
                      scrollbarWidth: "thin",
                      scrollbarColor: "#FF7F32 #FFF5E5", // Firefox
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#FF7F32", // orange scroll
                        borderRadius: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#FFF5E5",
                      },
                    },
                  }}
                  variant="outlined"
                />
              </div>

              {/* Actual Level */}

              <Div
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    mr: 1,
                    color: "#002F6C",
                  }}
                >
                  {t("evaluation-form.actualLevel")}:
                </Typography>

                <TextField
                  fullWidth
                  value={values.evaluation?.actualLevel2?.label || "---"}
                  disabled
                  sx={{
                    bgcolor: "#FFF5E5",
                    borderRadius: 2,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#002F6C", // keep text black when disabled
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              </Div>

              {/* Objective Level */}

              <Div
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  mb: 2,
                }}
              >
                {/* Label */}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    mr: 1,
                    color: "#002F6C",
                  }}
                >
                  {t("evaluation-form.targetLevel")}:
                </Typography>

                {/* Value Box */}
                <TextField
                  fullWidth
                  value={values.evaluation?.objectiveLevel2?.label || "---"}
                  disabled
                  sx={{
                    bgcolor: "#FFF5E5",
                    borderRadius: 2,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#002F6C", // keep text black when disabled
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              </Div>

              {/* Book Recommended */}

              <Div
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  mb: 2,
                }}
              >
                {/* Label */}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    mr: 1,
                    color: "#002F6C",
                  }}
                >
                  {t("evaluation-form.booksRecommended")}:
                </Typography>

                {/* Value Box */}
                <TextField
                  fullWidth
                  value={
                    values.evaluation?.booksRecommended &&
                    values.evaluation?.booksRecommended.length > 0
                      ? values.evaluation?.booksRecommended
                      : "---"
                  }
                  disabled
                  sx={{
                    bgcolor: "#FFF5E5",
                    borderRadius: 2,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#002F6C", // keep text black when disabled
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              </Div>

              {/* Teachers Recommended */}

              <div>
                <Typography
                  sx={{ fontWeight: "bold", mb: 0.5, color: "#002F6C" }}
                >
                  {t("evaluation-form.teachersRecommended")}:
                </Typography>

                <TextField
                  fullWidth
                  value={
                    values.evaluation?.teachers &&
                    values.evaluation.teachers.length > 0
                      ? values.evaluation.teachers
                          .map((item) => `${item.firstName} ${item.lastName}`)
                          .join("\n")
                      : "No teachers recommended"
                  }
                  disabled
                  multiline
                  minRows={1} // 🔑 will auto-grow but never shrink smaller than 1 row
                  maxRows={10} // optional limit so it doesn’t get too tall
                  sx={{
                    bgcolor: "#FFF5E5",
                    borderRadius: 2,
                    mb: 2,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#002F6C", // keep text black
                    },
                  }}
                  variant="outlined"
                />
              </div>

              {/* Program Frequency */}

              <Div
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  mb: 2,
                }}
              >
                {/* Label */}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    mr: 1,
                    color: "#002F6C",
                  }}
                >
                  {t("evaluation-form.frequencyRecommended")}:
                </Typography>

                {/* Value Box */}
                <TextField
                  fullWidth
                  value={
                    values.evaluation?.programFrequencyRecommended &&
                    values.evaluation?.programFrequencyRecommended.length > 0
                      ? values.evaluation?.programFrequencyRecommended
                      : "---"
                  }
                  disabled
                  sx={{
                    bgcolor: "#FFF5E5",
                    borderRadius: 2,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#002F6C", // keep text black when disabled
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              </Div>

              {/* Back Button */}
              {/*<Div sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  onClick={() => {
                    onClose();
                  }}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#2d6bbf", // standard MUI red
                    color: "#fff",
                    "&:hover": {
                      bgcolor: "#265ca6", // darker red on hover
                    },
                  }}
                >
                  {t("common.backBtn")}
                </Button>
              </Div>*/}
            </Div>

            {/*<Div
              sx={{
                display: "flex",
                flexDirection: "row",
                pt: 2,
                justifyContent: "center",
              }}
            >
              <Button
                onClick={() => {
                  onClose();
                }}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#2d6bbf", // standard MUI red
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#265ca6", // darker red on hover
                  },
                }}
              >
                {t("common.closeBtn")}
              </Button>
            </Div>*/}
          </Div>
        </FormControl>
      </Div>
    </Div>
  );
};

export default Evaluation;
